<script lang="ts">
  import { relativeTime } from '../format'
  import { checkConnection, connection } from '../state/connection.svelte'
  import { layout } from '../state/layout.svelte'
  import { location } from '../state/location.svelte'
  import Icon from './Icon.svelte'

  /**
   * Whether the app can reach its server: one inverse bar at the very top,
   * above the app, so it never covers a control (round 2's 3.7). It was a
   * pill over the map before, and on a phone that pill sat under the two
   * floating buttons, over the map's most-looked-at strip.
   *
   * Nothing is shown while live: a permanent "connected" bar is one more
   * thing that says nothing. What is shown instead is the change — the
   * outage while it lasts, and "Back online" for four seconds when it ends.
   */

  const RESTORED_MS = 4000

  /** Whether the bar is taking room, so the Shell can hand the notch to it. */
  let { shown = $bindable(false) }: { shown?: boolean } = $props()

  // "3 min ago" has to age on its own, as in StatusLine.
  let now = $state(new Date())
  $effect(() => {
    const timer = setInterval(() => (now = new Date()), 30_000)
    return () => clearInterval(timer)
  })

  let checking = $state(false)
  async function retry() {
    checking = true
    try {
      await checkConnection()
    } finally {
      checking = false
    }
  }

  // Not $state: only this effect reads it, and the first run must not flash
  // "Back online" for a page that was never offline.
  let seenRestored = connection.restored
  let restored = $state(false)
  $effect(() => {
    const n = connection.restored
    if (n === seenRestored) return
    seenRestored = n
    restored = true
    const timer = setTimeout(() => (restored = false), RESTORED_MS)
    return () => clearTimeout(timer)
  })

  let down = $derived(connection.status !== 'live')
  $effect(() => {
    shown = down || restored
  })
  let offline = $derived(connection.status === 'offline')
  let title = $derived(offline ? 'Offline' : 'Can’t reach the server')
  // What is on screen is the question a person actually has: can I trust it?
  let detail = $derived.by(() => {
    const age = relativeTime(location.fetchedAt, now)
    const shown = location.bundle && age ? `Showing the forecast from ${age}.` : ''
    if (offline) return shown ? `${shown} Nothing updates until you reconnect.` : 'Nothing will update until you reconnect.'
    const why = layout.isDesktop ? 'The network is up but Fusion WX is not answering.' : 'Your network is up.'
    return shown ? `${why} ${shown}` : `${why} Retrying every few seconds.`
  })
</script>

<!-- Always mounted: a live region announces changes to content it already
     holds, and one inserted together with its text is often not read out. -->
<div class="conn-wrap" class:phone={!layout.isDesktop} role="status" aria-live="polite">
  {#if down}
    <div class="conn down">
      <span class="mark" aria-hidden="true">
        <Icon name={offline ? 'wifi-off' : 'cloud-off'} size={18} strokeWidth={2.5} />
      </span>
      <span class="text">
        <strong>{title}</strong>
        <span class="detail">{detail}</span>
      </span>
      {#if connection.status === 'unreachable'}
        <button type="button" class="retry touch-target" onclick={retry} disabled={checking}>
          {checking ? 'Checking…' : layout.isDesktop ? 'Retry now' : 'Retry'}
        </button>
      {/if}
    </div>
  {:else if restored}
    <div class="conn up">
      <span class="mark ok" aria-hidden="true"><Icon name="check" size={18} strokeWidth={3} /></span>
      <span class="text">
        <strong>Back online</strong>
        <span class="detail">Radar, alerts and model runs are catching up.</span>
      </span>
    </div>
  {/if}
</div>

<style>
  /* In the Shell's column, above both layouts: it takes its own height and
     the app below it gives that height up, rather than being covered. */
  .conn {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 44px;
    padding: 0 24px;
    background: var(--color-text);
    color: var(--color-bg);
  }

  /* On a phone the bar carries the status bar inset, and the text stacks. */
  .phone .conn {
    align-items: center;
    gap: 10px;
    padding: calc(var(--safe-top) + 10px) 16px 10px;
  }

  .mark {
    flex: none;
    display: grid;
    place-items: center;
  }

  .mark.ok {
    color: var(--color-success-inverse);
  }

  .text {
    flex: 1;
    display: flex;
    align-items: baseline;
    gap: 12px;
    min-width: 0;
  }

  .phone .text {
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
  }

  strong {
    font-size: 14px;
    font-weight: 800;
    white-space: nowrap;
  }

  .phone strong {
    font-size: 15px;
  }

  .detail {
    font-size: 13px;
    line-height: 1.35;
    opacity: 0.8;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .phone .detail {
    white-space: normal;
  }

  .retry {
    flex: none;
    height: 28px;
    padding: 0 12px;
    border: 2px solid var(--color-bg);
    background: none;
    color: var(--color-bg);
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
  }

  .phone .retry {
    height: 44px;
    font-size: 14px;
  }

  .retry:disabled {
    opacity: 0.7;
    cursor: default;
  }
</style>
